// PhotoModal.tsx — el paso 'photos' (My photos) a pantalla grande.
// Qué hace: grilla multi-select de las fotos del workspace, con celda para subir
//   una nueva sin salir del flujo. Reusa el armazón del modal de estilos.
// Qué exporta: PhotoModal.
// Quién lo usa: widgets/PhotoPicker.tsx.

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Overlay, Panel, Head, HeadText, CloseBtn, Body, GridPane, PhotoGrid, PhotoCell,
  PhotoCheck, Foot, FootCount, FootActions, GhostBtn, PrimaryBtn, UploadTile,
  StepHead, StepNum, StepTitle,
} from '../styleModalStyled';

interface Props {
  open: boolean;
  onClose: () => void;
  myPhotos: { id: string; url: string; name?: string }[];
  selectedPhotoIds: string[];
  togglePhoto: (id: string) => void;
  confirmPhotos: () => void;
  uploadPhoto: (file: File) => void;
  photoUploading: boolean;
  /** Cuántos posts se van a generar — define cuántas fotos conviene elegir. */
  totalPosts: number;
}

const Check = () => (
  <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path d="M2.5 6.2l2.3 2.3 4.7-5" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export function PhotoModal({
  open, onClose, myPhotos, selectedPhotoIds, togglePhoto, confirmPhotos,
  uploadPhoto, photoUploading, totalPosts,
}: Props) {
  const [uploadError, setUploadError] = useState('');

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  const n = selectedPhotoIds.length;
  // Mismo criterio que AI Transfer: si hay menos fotos que posts, alguna se
  // repite — se dice acá en vez de que se descubra mirando los resultados.
  const hint = n === 0
    ? 'Pick at least one photo to continue.'
    : n >= totalPosts
      ? `Each of your ${totalPosts} post${totalPosts !== 1 ? 's' : ''} gets a different photo.`
      : `${n} photo${n !== 1 ? 's' : ''} for ${totalPosts} posts — they cycle, so some repeat. Pick ${totalPosts - n} more for one each.`;

  return createPortal(
    <Overlay onClick={onClose} role="dialog" aria-modal="true" aria-label="Pick your photos">
      <Panel onClick={(e) => e.stopPropagation()}>
        <Head>
          <HeadText>
            <h2>Pick the photos to use</h2>
            <p>Only these go into the posts — the AI will not pull anything else.</p>
          </HeadText>
          <CloseBtn type="button" onClick={onClose} aria-label="Close">
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
            </svg>
          </CloseBtn>
        </Head>

        <Body style={{ gridTemplateColumns: 'minmax(0, 1fr)' }}>
          <GridPane>
            <StepHead>
              <StepNum $done={n > 0}>1</StepNum>
              <StepTitle>Your photos <span>— {hint}</span></StepTitle>
            </StepHead>

            <PhotoGrid style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
              {/* Subir va primero: sin esto hay que salir del flujo, ir a
                  Brand → Assets y volver. */}
              <UploadTile title="Upload a photo from your computer">
                <span className="plus">＋</span>
                {photoUploading ? 'Uploading…' : 'Upload a photo'}
                <input
                  type="file" accept="image/*" disabled={photoUploading}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    if (!f) return;
                    if (!f.type.startsWith('image/')) { setUploadError('That file is not an image.'); return; }
                    setUploadError('');
                    uploadPhoto(f);
                  }}
                />
              </UploadTile>

              {myPhotos.map((ph) => {
                const sel = selectedPhotoIds.includes(ph.id);
                return (
                  <PhotoCell
                    key={ph.id} type="button" $active={sel} aria-pressed={sel}
                    title={ph.name || 'Photo'} onClick={() => togglePhoto(ph.id)}
                  >
                    <img src={ph.url} alt="" loading="lazy" />
                    {sel && <PhotoCheck><Check /></PhotoCheck>}
                  </PhotoCell>
                );
              })}
            </PhotoGrid>

            {uploadError && (
              <div style={{ fontSize: 12, color: '#B91C1C', marginTop: 10 }}>{uploadError}</div>
            )}
            {myPhotos.length === 0 && !photoUploading && (
              <div style={{ fontSize: 13, color: '#6B7280', marginTop: 14 }}>
                No photos in this workspace yet — upload one above.
              </div>
            )}
          </GridPane>
        </Body>

        <Foot>
          <FootCount>
            <strong>{n}</strong> photo{n !== 1 ? 's' : ''} selected
          </FootCount>
          <FootActions>
            <GhostBtn type="button" onClick={onClose}>Cancel</GhostBtn>
            <PrimaryBtn
              type="button" disabled={n === 0}
              onClick={() => { confirmPhotos(); onClose(); }}
            >
              {n === 0 ? 'Pick at least one photo' : `Use ${n} photo${n !== 1 ? 's' : ''}`}
            </PrimaryBtn>
          </FootActions>
        </Foot>
      </Panel>
    </Overlay>,
    document.body
  );
}
